import { Fragment, type FC, type ReactNode } from "react";
import Stack from "@mui/material/Stack";
import type { Section as SectionType, Form, QuestionCard } from "../types";
import { logUnhandled } from "../../api/logError";
import { TraverseQuestionCardComponentBasedOnRule } from "../shared/TraverseQuestionCard";
import { DynamicInput } from "../DynamicInput";
import { InlineFieldPrimary, InlineFieldSecondary } from "./InlineField";
import { Box, Divider, Typography } from "@mui/material";
import type { AnswerSnapshot as AnswerSnapshotType } from "../../dynamicHistory/types";
import { AnswerSnapshot } from "../../dynamicHistory/HistoryEntries";

type PreviousAnswerInfo = { questionCardId: string; snapshot: AnswerSnapshotType };

type InlineFormVariant = "primary" | "secondary";
type InlineFormRenderSlots = {
  afterSection?: (section: SectionType) => ReactNode;
  afterInput?: (questionCard: QuestionCard) => ReactNode;
  replaceQuestion?: (questionCard: QuestionCard) => ReactNode | undefined;
};

type InlineFormProps = {
  form: Form;
  id: string;
  variant?: InlineFormVariant;
  renderSlots?: InlineFormRenderSlots;
  previousAnswersInfo?: Array<PreviousAnswerInfo>;
};
export const InlineForm: FC<InlineFormProps> = ({
  form,
  id,
  variant = "primary",
  renderSlots = {},
  previousAnswersInfo = [],
}) => {
  const previousAnswersInfoMap = new Map(
    previousAnswersInfo.map(answerInfo => [answerInfo.questionCardId, answerInfo])
  );
  return (
    <Stack key={id} spacing={5}>
      {form.sections.map(section => (
        <Fragment key={section.id}>
          <Section
            section={section}
            variant={variant}
            renderSlots={renderSlots}
            previousAnswersInfoMap={previousAnswersInfoMap}
          />
          {renderSlots.afterSection?.(section)}
        </Fragment>
      ))}
    </Stack>
  );
};

type SectionProps = {
  section: SectionType;
  variant: InlineFormVariant;
  renderSlots?: InlineFormRenderSlots;
  previousAnswersInfoMap: Map<string, PreviousAnswerInfo>;
};
const Section: FC<SectionProps> = ({ section, variant, renderSlots = {}, previousAnswersInfoMap }) => {
  const { name, questionCards } = section;

  return (
    <InlineFormSection title={name} variant={variant}>
      {questionCards.map(rootQuestionCard => (
        <TraverseQuestionCardComponentBasedOnRule
          key={rootQuestionCard.id}
          rootQuestionCard={rootQuestionCard}
          render={questionCard => {
            const replacement = renderSlots.replaceQuestion?.(questionCard);
            if (replacement !== undefined) return replacement;
            const { header, questionText, helpInformation, extraHelpInformation, id } = questionCard;
            const afterInput = renderSlots.afterInput?.(questionCard);
            const previousAnswerInfo = previousAnswersInfoMap.get(questionCard.id);
            const input = (
              <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 0.5 }}>
                <DynamicInput variant="inline" questionCard={questionCard} />
                {previousAnswerInfo && (
                  <Box sx={{ mt: 1 }}>
                    <AnswerSnapshot answerSnapshot={previousAnswerInfo.snapshot} variant="diff" />
                  </Box>
                )}
                {afterInput && <Box>{afterInput}</Box>}
              </Box>
            );

            switch (variant) {
              case "primary":
                return (
                  <InlineFieldPrimary
                    htmlFor={id}
                    label={header}
                    input={input}
                    infoPopoverContent={{
                      title: questionText,
                      body: helpInformation,
                      info: extraHelpInformation,
                    }}
                  />
                );
              case "secondary":
                return (
                  <InlineFieldSecondary htmlFor={id} label={questionText} subLabel={helpInformation} input={input} />
                );
              default:
                return logUnhandled(variant, "variant");
            }
          }}
        />
      ))}
    </InlineFormSection>
  );
};

type InlineFormSectionProps = {
  title: ReactNode;
  subtitle?: string;
  children: ReactNode;
  variant?: InlineFormVariant;
};

export const InlineFormSection: FC<InlineFormSectionProps> = ({ title, subtitle, children, variant = "primary" }) => {
  return (
    <Stack>
      {variant === "primary" ? (
        <Stack sx={{ pb: 1 }}>
          <Typography variant="h5">{title}</Typography>
          {subtitle && (
            <Typography
              sx={{
                fontSize: theme => theme.typography.pxToRem(12),
                color: theme => theme.palette.text.tertiary,
                maxWidth: "60ch",
                textWrap: "pretty",
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Stack>
      ) : (
        <>
          <Typography variant="h2" sx={{ pb: 1, fontSize: theme => theme.typography.pxToRem(21) }}>
            {title}
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </>
      )}
      {children}
    </Stack>
  );
};
